import { parseAiMapAction, parseAiMapPlan, type AiMapAction, type AiMapPlan } from './ai-map.js';
import type { SchemaInput } from './schema-input.js';
import { isSchemaRecord, isString } from './runtime-values.js';
import { geographicSourceForPoints, parseDrawingGeographicSource } from './drawing-geographic-source.js';

export const AI_MAP_TASK_OUTCOMES = ['complete', 'partial', 'answered', 'clarification', 'unsupported', 'failed', 'unclassified'] as const;
export type AiMapTaskOutcome = typeof AI_MAP_TASK_OUTCOMES[number];
export const AI_MAP_TASK_CLASSES = ['deterministic_edit', 'question', 'place_lookup', 'route', 'area', 'complex', 'unclassified'] as const;
export type AiMapTaskClass = typeof AI_MAP_TASK_CLASSES[number];
export type AiMapTaskResult = { version: 2; outcome: AiMapTaskOutcome; taskClass: AiMapTaskClass };

export function aiMapTask(outcome: AiMapTaskOutcome, taskClass: AiMapTaskClass): AiMapTaskResult {
  return { version: 2, outcome, taskClass };
}

export function parseAiMapTask(value: SchemaInput): AiMapTaskResult | null {
  if (!isSchemaRecord(value) || value.version !== 2) return null;
  const outcome = AI_MAP_TASK_OUTCOMES.find((item) => item === value.outcome);
  const taskClass = AI_MAP_TASK_CLASSES.find((item) => item === value.taskClass);
  return outcome && taskClass ? aiMapTask(outcome, taskClass) : null;
}

/** Accept source labels only from stored server results or trusted tool output. */
export function parseTrustedAiMapAction(value: SchemaInput, existing: AiMapAction[] = []): AiMapAction | null {
  const action = parseAiMapAction(value, existing);
  if (action?.type === 'freehand' && isSchemaRecord(value) && (value.source === 'route' || value.source === 'geometry')) return { ...action, source: value.source };
  if (action?.type === 'polygon' && isSchemaRecord(value)) {
    const source = geographicSourceForPoints(parseDrawingGeographicSource(value.geographicSource), action.points);
    if (source) return { ...action, geographicSource: source };
  }
  return action;
}

/** Model and user input must use parseAiMapPlan, which discards server metadata. */
export function parseTrustedAiMapPlan(value: SchemaInput): AiMapPlan | null {
  const plan = parseAiMapPlan(value);
  if (!plan || !isSchemaRecord(value) || !Array.isArray(value.actions)) return null;
  plan.actions = value.actions.map((action, index) => parseTrustedAiMapAction(action, plan.actions.slice(0, index))!);
  const task = parseAiMapTask(value.task);
  if (task) plan.task = task;
  if (isSchemaRecord(value.receipt) && Array.isArray(value.receipt.sources)) {
    const allowed = ['place', 'route', 'boundary', 'dataset', 'map_data', 'travel_time'] as const;
    const sources = value.receipt.sources.flatMap((value) => allowed.filter((source) => source === value));
    const target = value.receipt.targetPlace;
    plan.receipt = { sources: [...new Set(sources)] };
    if (isString(target) && target.trim() && target.length <= 120) plan.receipt.targetPlace = target.trim();
  }
  return plan;
}
